import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Button,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Typography,
    Grid,
    FormControlLabel,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert
} from '@mui/material';
import { Add, Delete, Warning } from '@mui/icons-material';
import LevelSelector from '../shared/LevelSelector';

const AlgorithmicProblemForm = ({ problem, languages = [], onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        problemUrl: '',
        difficulty: 'medium',
        language: '',
        level: '',
        tags: [],
        taskContent: '',
        solutionsContent: [''],
        isReadyToServe: false
    });

    const [tagInput, setTagInput] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [readyToServeDialog, setReadyToServeDialog] = useState({ open: false, hasChecked: false });
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (problem) {
            setFormData({
                title: problem.title || '',
                problemUrl: problem.problemUrl || '',
                difficulty: problem.difficulty || 'medium',
                language: problem.language?._id || '',
                level: problem.level?._id || '',
                tags: problem.tags || [],
                taskContent: problem.task?.text || '',
                solutionsContent: [], // Don't load solutions in form - they're managed separately
                isReadyToServe: problem.isReadyToServe || false
            });
        }
    }, [problem]);

    // Effect to validate and update language selection when languages are loaded
    useEffect(() => {
        if (languages.length > 0 && formData.language) {
            const languageExists = languages.some(lang => lang._id === formData.language);
            if (!languageExists) {
                setFormData(prev => ({
                    ...prev,
                    language: ''
                }));
            }
        }
    }, [languages, formData.language]);

    const cleanHtml = (rawHtml) => {
        if (!rawHtml) return '';
        return rawHtml
            // collapse 2+ blank lines into a single newline
            .replace(/\n{2,}/g, '\n')
            // optionally remove all newlines between tags
            .replace(/>\s+</g, '><');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (isMountedRef.current) {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
            
            if (errors[name]) {
                setErrors(prev => ({
                    ...prev,
                    [name]: ''
                }));
            }
        }
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim()) && isMountedRef.current) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tagInput.trim()]
            }));
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        if (isMountedRef.current) {
            setFormData(prev => ({
                ...prev,
                tags: prev.tags.filter(tag => tag !== tagToRemove)
            }));
        }
    };

    const handleTagInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.problemUrl.trim()) {
            newErrors.problemUrl = 'Problem URL is required';
        }

        if (!formData.language) {
            newErrors.language = 'Language is required';
        }

        if (!formData.level) {
            newErrors.level = 'Learner level is required';
        }

        if (!formData.taskContent.trim()) {
            newErrors.taskContent = 'Task content is required';
        }

        if (formData.tags.length === 0) {
            newErrors.tags = 'At least one tag is required';
        }

        if (isMountedRef.current) {
            setErrors(newErrors);
        }
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        if (isMountedRef.current) {
            setLoading(true);
        }
        
        try {
            // Don't send solutionsContent - solutions are managed separately in content manager
            const { solutionsContent, ...formDataToSubmit } = formData;
            await onSubmit(formDataToSubmit);
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    const handleReadyToServeToggle = () => {
        if (!formData.isReadyToServe) {
            setReadyToServeDialog({ open: true, hasChecked: false });
        } else {
            if (isMountedRef.current) {
                setFormData(prev => ({
                    ...prev,
                    isReadyToServe: false
                }));
            }
        }
    };

    const handleReadyToServeConfirm = (confirmed) => {
        setReadyToServeDialog({ open: false, hasChecked: false });
        if (confirmed && isMountedRef.current) {
            setFormData(prev => ({
                ...prev,
                isReadyToServe: true
            }));
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        error={!!errors.title}
                        helperText={errors.title}
                        required
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Problem URL"
                        name="problemUrl"
                        value={formData.problemUrl}
                        onChange={handleInputChange}
                        error={!!errors.problemUrl}
                        helperText={errors.problemUrl || 'URL to the actual problem (e.g., LeetCode link)'}
                        required
                        type="url"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.language}>
                        <InputLabel>Language</InputLabel>
                        <Select
                            name="language"
                            value={
                                languages.length === 0 
                                    ? ''
                                    : languages.some(lang => lang._id === formData.language) 
                                        ? formData.language
                                        : ''
                            }
                            onChange={handleInputChange}
                            label="Language"
                            required
                            disabled={languages.length === 0}
                        >
                            {languages.length === 0 ? (
                                <MenuItem value="" disabled>
                                    Loading languages...
                                </MenuItem>
                            ) : (
                                languages.map((language) => (
                                    <MenuItem key={language._id} value={language._id}>
                                        {language.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                        {errors.language && (
                            <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                                {errors.language}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Difficulty</InputLabel>
                        <Select
                            name="difficulty"
                            value={formData.difficulty}
                            onChange={handleInputChange}
                            label="Difficulty"
                            required
                        >
                            <MenuItem value="easy">Easy</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="hard">Hard</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <LevelSelector
                        value={formData.level}
                        onChange={(e) => handleInputChange({ target: { name: 'level', value: e.target.value } })}
                        error={errors.level}
                        required
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Publication Settings
                        </Typography>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isReadyToServe}
                                    onChange={handleReadyToServeToggle}
                                    name="isReadyToServe"
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1">
                                        Ready to Serve
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formData.isReadyToServe 
                                            ? "This problem is ready for users to view" 
                                            : "This problem is not ready for users to view"
                                        }
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                </Grid>

                {/* Task Content */}
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        Task Description *
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={12}
                        label="Enter the problem task description here..."
                        name="taskContent"
                        value={formData.taskContent}
                        onChange={handleInputChange}
                        error={!!errors.taskContent}
                        helperText={errors.taskContent || 'Write the complete problem description in this field'}
                        required
                        sx={{
                            '& .MuiInputBase-root': {
                                fontSize: '1.1rem',
                                lineHeight: 1.6
                            }
                        }}
                        placeholder="Problem description..."
                    />
                </Grid>

                {/* Solutions Note */}
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Solutions can be added and managed separately after creating the problem. 
                            Use the "Edit Content" button on the problem list to manage solutions.
                        </Typography>
                    </Alert>
                </Grid>

                {/* Tags */}
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Typography variant="subtitle1">
                            Tags {errors.tags && <span style={{ color: 'red', fontSize: '0.8rem' }}>({errors.tags})</span>}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <TextField
                            label="Add tag"
                            value={tagInput}
                            onChange={(e) => {
                                if (isMountedRef.current) {
                                    setTagInput(e.target.value);
                                }
                            }}
                            onKeyPress={handleTagInputKeyPress}
                            size="small"
                            sx={{ flexGrow: 1 }}
                        />
                        <IconButton 
                            onClick={handleAddTag}
                            disabled={!tagInput.trim()}
                            color="primary"
                        >
                            <Add />
                        </IconButton>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {formData.tags.map((tag, index) => (
                            <Chip
                                key={index}
                                label={tag}
                                onDelete={() => handleRemoveTag(tag)}
                                deleteIcon={<Delete />}
                                variant="outlined"
                            />
                        ))}
                    </Box>
                </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <Button onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (problem ? 'Update' : 'Create')}
                </Button>
            </Box>

            {/* Ready to Serve Confirmation Dialog */}
            <Dialog
                open={readyToServeDialog.open}
                onClose={() => handleReadyToServeConfirm(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Warning color="warning" />
                        Confirm Ready to Serve
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Before marking this problem as "Ready to Serve", please ensure you have:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mt: 2 }}>
                        <li>
                            <Typography variant="body2">
                                Reviewed the problem content for quality and completeness
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Verified all metadata (title, URL, tags, etc.) is correct
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Added at least one solution explanation
                            </Typography>
                        </li>
                    </Box>
                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
                        Have you completed these checks and is the problem ready for users?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleReadyToServeConfirm(false)}>
                        No, I need to check more
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => handleReadyToServeConfirm(true)}
                    >
                        Yes, it is ready
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AlgorithmicProblemForm;

